import { CacheProvider } from '@emotion/react';
import { CssBaseline, createTheme, GlobalStyles, ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import { ClerkServiceErrorDialog } from './components/clerk-service-error-dialog';
import { ConsultantNotActivePartnerDialog } from './components/consultant-not-active-partner-dialog';
import { ErrorBoundary } from './components/error-boundary';
import { NoConnectionScreen } from './components/no-connection-screen';
import { PwaInstallBanner } from './components/pwa-install-banner';
import { UpdateRequiredScreen } from './components/update-required-screen';
import { DatePickerProvider } from './config/date-picker-provider';
import { createEmotionCache } from './config/emotion-cache';
import i18n, { getLanguageDir } from './config/i18n';
import { shouldRetryQuery } from './config/query-retry';
import { useAnalyticsConsentSync } from './hooks/analytics/use-analytics-consent-sync';
import { useSignupTracking } from './hooks/analytics/use-signup-tracking';
import { useNetworkStatus } from './hooks/use-network-status';
import { useVersionCheck } from './hooks/use-version-check';
import { AppRouter } from './router/main-router';
import { registerReloadBlocker } from './services/pwa/reload-gate';
import { aiTheme } from './theme/ai-theme';

// Writing direction is resolved once at startup from the persisted language.
// Switching to/from a RTL language triggers a one-time reload (see config/i18n.ts)
// so the Emotion cache and theme direction stay consistent across the whole tree.
const initialDir = getLanguageDir(i18n.language);
const emotionCache = createEmotionCache(initialDir);
const directedTheme = initialDir === 'rtl' ? createTheme(aiTheme, { direction: 'rtl' }) : aiTheme;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      retry: shouldRetryQuery,
    },
  },
});

// Automatic update reloads wait for in-flight mutations (HACCP records being
// saved) — see services/pwa/reload-gate.ts.
registerReloadBlocker(() => queryClient.isMutating() > 0);

const AppContent = () => {
  const { isOnline } = useNetworkStatus();
  const { updateRequired, dismissUpdate, clearAndReload } = useVersionCheck();
  // Mounted here, above every route (sign-up/onboarding sit outside the
  // protected layout), so consent sync + sign_up detection run for the whole
  // acquisition funnel. MainLayout keeps its own consent sync — idempotent.
  useAnalyticsConsentSync();
  useSignupTracking();

  return (
    <>
      <UpdateRequiredScreen
        visible={updateRequired}
        onUpdate={clearAndReload}
        onDismiss={dismissUpdate}
      />
      <NoConnectionScreen visible={!isOnline} />
      <ErrorBoundary level="app">
        <AppRouter />
      </ErrorBoundary>
      <ClerkServiceErrorDialog />
      <ConsultantNotActivePartnerDialog />
      <PwaInstallBanner />
    </>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <CacheProvider value={emotionCache}>
        <ThemeProvider theme={directedTheme}>
          <CssBaseline />
          <DatePickerProvider>
            <GlobalStyles
              styles={{
                '.prospero-snackbar-top-center': {
                  top: 'calc(env(safe-area-inset-top, 0px) + 80px) !important',
                },
              }}
            />
            <SnackbarProvider
              maxSnack={3}
              autoHideDuration={4000}
              disableWindowBlurListener
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              classes={{ containerAnchorOriginTopCenter: 'prospero-snackbar-top-center' }}
              transitionDuration={{ enter: 225, exit: 225 }}
            >
              <AppContent />
            </SnackbarProvider>
          </DatePickerProvider>
        </ThemeProvider>
      </CacheProvider>
    </QueryClientProvider>
  );
}

export default App;
